import Legend from "./Legend/index.js";
import identity from "../util/identity.js"; // eslint-disable-next-line @typescript-eslint/no-explicit-any
import { jsx as _jsx } from "react/jsx-runtime";
const formatZero = label => label === 0 ? '0' : label || '';
/** Default transform implicitly assumes that Datum is of type number. */
function defaultTransform(_ref) {
  let {
    labelDelimiter,
    labelLower,
    labelUpper
  } = _ref;
  return _ref2 => {
    let {
      scale,
      labelFormat
    } = _ref2;
    const scaleRange = scale.range();
    return (d, i) => {
      const [d0, d1] = scaleRange.length >= i ? scale.invertExtent(scaleRange[i]) : [undefined, undefined];
      let delimiter = ` ${labelDelimiter} `;
      let text = '';
      let value;
      if (d0 == null && typeof d1 === 'number') {
        // lower threshold e.g., [undefined, number]
        delimiter = labelLower || delimiter;
        value = d1 - Math.abs(2 * d1 - 1); // guarantees a value smaller than the lower threshold
        text = `${delimiter}${formatZero(labelFormat(d1, i))}`;
      } else if (d0 != null && d1 != null) {
        // threshold step
        value = d;
        text = `${formatZero(labelFormat(d0, i))}${delimiter}${formatZero(labelFormat(d1, i))}`;
      } else if (typeof d0 === 'number' && d1 == null) {
        // upper threshold e.g., [number, undefined]
        delimiter = labelUpper || delimiter;
        value = d0 + Math.abs(2 * d0 + 1); // // guarantees a value larger than the upper threshold
        text = `${delimiter}${formatZero(labelFormat(d0, i))}`;
      }
      return {
        extent: [d0, d1],
        value: scale(value),
        text,
        datum: d,
        index: i
      };
    };
  };
}

/**
 * A Threshold legend component for threshold scales.
 * Threshold scales map continuous input values to discrete output values based on threshold boundaries.
 * This component displays the threshold ranges with customizable delimiters and labels.
 */
export default function Threshold(_ref3) {
  let {
    scale,
    domain: inputDomain,
    labelFormat = identity,
    labelTransform: inputLabelTransform,
    labelDelimiter = 'to',
    labelLower = 'Less than ',
    labelUpper = 'More than ',
    ...restProps
  } = _ref3;
  // d3 docs specify that for n values in a domain, there should be n+1 values in the range
  // https://github.com/d3/d3-scale#threshold_domain
  // therefore if a domain is not specified we transform the range into input values
  // because it should contain more elements

  const domain = inputDomain || scale.range().map(output => scale.invertExtent(output)[0]);
  const labelTransform = inputLabelTransform || defaultTransform({
    labelDelimiter,
    labelLower,
    labelUpper
  });
  return /*#__PURE__*/_jsx(Legend, {
    scale: scale,
    domain: domain,
    labelFormat: labelFormat,
    labelTransform: labelTransform,
    ...restProps
  });
}